import {
  InsightsTimeframes,
  navigableInsightsTimeframes,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { weekdaysNumberMap } from '@griply/utils';
import {
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  isSameDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type StartFunc = (date: Timestamp, weekStartsOn: WeekDays) => Timestamp;
type AddFunc = (date: Timestamp, amount: number) => Timestamp;

const startFuncs: Record<InsightsTimeframes, StartFunc> = {
  [InsightsTimeframes.Week]: (date, weekStartsOn) =>
    startOfWeek(date, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }),
  [InsightsTimeframes.Month]: (date) => startOfMonth(date),
  [InsightsTimeframes.Quarter]: (date) => startOfQuarter(date),
  [InsightsTimeframes.Year]: (date) => startOfYear(date),
  [InsightsTimeframes.FourWeeks]: (date, weekStartsOn) =>
    startOfWeek(date, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }),
  [InsightsTimeframes.TwelveWeeks]: (date, weekStartsOn) =>
    startOfWeek(date, { weekStartsOn: weekdaysNumberMap[weekStartsOn] }),
  [InsightsTimeframes.TwelveMonths]: (date) => startOfMonth(date),
};

const addFuncs: Record<InsightsTimeframes, AddFunc> = {
  [InsightsTimeframes.Week]: addWeeks,
  [InsightsTimeframes.Month]: addMonths,
  [InsightsTimeframes.Quarter]: addQuarters,
  [InsightsTimeframes.Year]: addYears,
  [InsightsTimeframes.FourWeeks]: addWeeks,
  [InsightsTimeframes.TwelveWeeks]: addWeeks,
  [InsightsTimeframes.TwelveMonths]: addMonths,
};

export type Options = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export type TimeframeNavigation = {
  referenceDate: Timestamp;
  onPrev: () => void;
  onToday: () => void;
  onNext: () => void;
  isNextDisabled: boolean;
};

export const useTimeframeNavigation = (
  timeframe: InsightsTimeframes,
  { today, weekStartsOn }: Options,
): TimeframeNavigation => {
  const normalizedTimeframe = navigableInsightsTimeframes.includes(timeframe)
    ? timeframe
    : InsightsTimeframes.Month;

  const currentPeriodStart = useMemo(
    () => startFuncs[normalizedTimeframe](today, weekStartsOn),
    [normalizedTimeframe, today, weekStartsOn],
  );

  const [referenceDate, setReferenceDate] = useState<Timestamp>(
    () => currentPeriodStart,
  );

  const onPrev = useCallback(() => {
    setReferenceDate((current) => addFuncs[normalizedTimeframe](current, -1));
  }, [normalizedTimeframe]);

  const onToday = useCallback(() => {
    setReferenceDate(currentPeriodStart);
  }, [currentPeriodStart]);

  const onNext = useCallback(() => {
    setReferenceDate((current) => {
      const next = addFuncs[normalizedTimeframe](current, 1);
      if (next > currentPeriodStart) {
        return current;
      }
      return next;
    });
  }, [currentPeriodStart, normalizedTimeframe]);

  const isNextDisabled = isSameDay(referenceDate, currentPeriodStart);

  // Reset reference date when the timeframe changes so the user always starts
  // looking at the current period after switching W/M/Q/Y. We track the
  // previous timeframe with a ref so today advancing doesn't trigger a reset.
  const previousTimeframeRef = useRef(normalizedTimeframe);
  useEffect(() => {
    if (previousTimeframeRef.current === normalizedTimeframe) {
      return;
    }
    previousTimeframeRef.current = normalizedTimeframe;
    setReferenceDate(currentPeriodStart);
  }, [normalizedTimeframe, currentPeriodStart]);

  return {
    referenceDate,
    onPrev,
    onToday,
    onNext,
    isNextDisabled,
  };
};
